import { useCallback } from 'react';
import { Product } from '../../types/product';

interface UseProductActionsProps {
  newProduct: any;
  editProduct: Partial<Product> | null;
  originalHandleAdd: () => Promise<boolean>;
  originalHandleEditSave: () => Promise<boolean>;
  originalHandleDelete: (product: Product) => Promise<boolean>;
  confirm: (options: any) => Promise<boolean>;
  showToast: (options: any) => void;
}

export const useProductActions = ({
  newProduct,
  editProduct,
  originalHandleAdd,
  originalHandleEditSave,
  originalHandleDelete,
  confirm,
  showToast,
}: UseProductActionsProps) => {
  const validateProduct = useCallback((product: Product | any): string | null => {
    if (!product.name?.trim()) {
      return 'Ürün adı zorunludur.';
    }
    if (!product.description?.trim()) {
      return 'Ürün açıklaması zorunludur.';
    }
    if (!product.price || product.price <= 0) {
      return 'Geçerli bir fiyat giriniz.';
    }
    if (!product.main_category_id || !product.category_id) {
      return 'Ana kategori ve alt kategori seçimi zorunludur.';
    }
    return null;
  }, []);

  const handleAdd = useCallback(async () => {
    const validationError = validateProduct(newProduct);
    if (validationError) {
      showToast({
        type: 'error',
        title: 'Hata!',
        message: validationError,
      });
      return;
    }

    const confirmed = await confirm({
      title: 'Ürün Ekle',
      message: `"${newProduct.name}" adlı ürünü eklemek istediğinizden emin misiniz?`,
      confirmText: 'Evet, Ekle',
      cancelText: 'İptal',
      type: 'info',
    });

    if (confirmed) {
      try {
        const success = await originalHandleAdd();
        if (success) {
          showToast({
            type: 'success',
            title: 'Başarılı!',
            message: `"${newProduct.name}" ürünü başarıyla eklendi.`,
          });
        }
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Hata!',
          message: 'Ürün eklenirken bir hata oluştu.',
        });
      }
    }
  }, [newProduct, validateProduct, confirm, showToast, originalHandleAdd]);

  const handleEditSave = useCallback(async () => {
    if (!editProduct) {
      return;
    }

    const validationError = validateProduct(editProduct);
    if (validationError) {
      showToast({
        type: 'error',
        title: 'Hata!',
        message: validationError,
      });
      return;
    }

    const confirmed = await confirm({
      title: 'Değişiklikleri Kaydet',
      message: `"${editProduct.name}" ürününde yapılan değişiklikleri kaydetmek istediğinizden emin misiniz?`,
      confirmText: 'Evet, Kaydet',
      cancelText: 'İptal',
      type: 'warning',
    });

    if (confirmed) {
      try {
        const success = await originalHandleEditSave();
        if (success) {
          showToast({
            type: 'success',
            title: 'Başarılı!',
            message: `"${editProduct.name}" ürünü başarıyla güncellendi.`,
          });
        }
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Hata!',
          message: 'Ürün güncellenirken bir hata oluştu.',
        });
      }
    }
  }, [editProduct, validateProduct, confirm, showToast, originalHandleEditSave]);

  const handleConfirmDelete = useCallback(
    async (product: Product) => {
      const confirmed = await confirm({
        title: 'Ürünü Sil',
        message: `"${product.name}" ürünü kalıcı olarak silinecek. Bu işlem geri alınamaz!`,
        confirmText: 'Evet, Sil',
        cancelText: 'İptal',
        type: 'danger',
      });

      if (confirmed) {
        try {
          const success = await originalHandleDelete(product);
          if (success) {
            showToast({
              type: 'success',
              title: 'Başarılı!',
              message: `"${product.name}" ürünü başarıyla silindi.`,
            });
          }
        } catch (error) {
          showToast({
            type: 'error',
            title: 'Hata!',
            message: 'Ürün silinirken bir hata oluştu.',
          });
        }
      }
    },
    [confirm, showToast, originalHandleDelete]
  );

  return {
    handleAdd,
    handleEditSave,
    handleConfirmDelete,
  };
};
